import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageSquare, Search, ChevronUp, Plus, Users, Trophy, Clock, Tag } from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { name: 'Prévention & Droits', icon: '💜' },
  { name: 'Santé & Bien-être', icon: '🧠' },
  { name: 'Famille & Éducation', icon: '👨‍👩‍👧' },
  { name: 'Compétences & Travail', icon: '💼' },
  { name: 'Culture & Société', icon: '🌍' },
  { name: 'Aide & Support Kurso', icon: '🛠️' },
];
const TYPE_ICONS = { question: '❓', temoignage: '💬', ressource: '📎', idee: '💡' };
const BADGE_LABELS = {
  admin: { label: 'Équipe Kurso 🏷️', color: 'bg-[#ff6b6b]/20 text-[#ff6b6b]' },
  moderator: { label: 'Modérateur 🛡️', color: 'bg-[#ffd93d]/20 text-[#ffd93d]' },
  expert: { label: 'Expert ⭐', color: 'bg-[#06d6a0]/20 text-[#06d6a0]' },
};

function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  return `il y a ${Math.floor(hours / 24)} j`;
}

function PostCard({ post, onClick }) {
  return (
    <button onClick={onClick} data-testid={`post-card-${post.post_id}`}
      className="w-full text-left bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/20 rounded-2xl p-4 transition-all space-y-2">
      <div className="flex items-start gap-2">
        <span className="text-lg leading-none mt-0.5">{post.type_icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {post.is_pinned && <span className="text-xs bg-[#ffd93d]/20 text-[#ffd93d] px-1.5 py-0.5 rounded-full">📌 Épinglé</span>}
            {post.is_closed && <span className="text-xs bg-white/10 text-[#a0a0ab] px-1.5 py-0.5 rounded-full">🔒 Fermé</span>}
            <span className="text-xs text-[#a0a0ab]">{post.category}</span>
          </div>
          <p className="text-white text-sm font-medium line-clamp-2">{post.title}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-[#a0a0ab]">
            <span>{post.author_name}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><ChevronUp size={10} /> {post.upvote_count || 0}</span>
            <span className="flex items-center gap-1"><MessageSquare size={10} /> {post.reply_count || 0}</span>
            <span className="ml-auto">{timeAgo(post.last_activity || post.created_at)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function CommunityForum() {
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const navigate = useNavigate();
  const { user } = useAuth();

  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') || 'recent';
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, sort };
        if (category) params.category = category;
        if (search) params.search = search;
        const [postsRes, leadersRes] = await Promise.all([
          axios.get(`${API}/api/forum/posts`, { params }),
          axios.get(`${API}/api/forum/leaderboard`)
        ]);
        setPosts(postsRes.data.posts);
        setTotal(postsRes.data.total);
        setPages(postsRes.data.pages);
        setLeaders(leadersRes.data.leaders);
      } catch { } finally { setLoading(false); }
    };
    fetchData();
  }, [category, sort, search, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams(p => { const n = new URLSearchParams(p); n.set('search', searchInput); n.set('page', '1'); return n; });
  };

  return (
    <Layout>
      <Helmet>
        <title>Communauté — Kurso</title>
        <meta name="description" content="Rejoignez la communauté Kurso — partagez, apprenez, échangez." />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">Communauté</h1>
            <p className="text-[#a0a0ab] text-sm">{total} discussions</p>
          </div>
          {user && (
            <button onClick={() => navigate('/communaute/nouveau')}
              className="btn-primary flex items-center gap-2 text-sm py-2 px-4"
              data-testid="create-post-btn">
              <Plus size={14} /> Nouvelle discussion
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-4 gap-4">
          {/* Main content */}
          <div className="lg:col-span-3 space-y-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0ab]" />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder="Rechercher une discussion..."
                className="w-full bg-white/5 border border-white/10 text-white placeholder-[#a0a0ab] rounded-2xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                data-testid="forum-search"
              />
            </form>

            {/* Categories */}
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
              <button onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.delete('category'); return n; })}
                className={`text-xs px-3 py-1.5 rounded-full flex-shrink-0 border transition-colors ${!category ? 'bg-[#ff6b6b] text-white border-[#ff6b6b]' : 'bg-white/5 text-[#e8e8e8] border-white/10'}`}>
                Tout
              </button>
              {CATEGORIES.map(cat => (
                <button key={cat.name}
                  onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('category', cat.name); return n; })}
                  className={`text-xs px-3 py-1.5 rounded-full flex-shrink-0 border whitespace-nowrap transition-colors ${category === cat.name ? 'bg-[#ff6b6b] text-white border-[#ff6b6b]' : 'bg-white/5 text-[#e8e8e8] border-white/10'}`}>
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div className="flex gap-2 text-xs">
              {['recent', 'popular', 'unanswered'].map(s => (
                <button key={s}
                  onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('sort', s); return n; })}
                  className={`px-3 py-1.5 rounded-lg border transition-colors ${sort === s ? 'bg-white/10 text-white border-white/20' : 'text-[#a0a0ab] border-white/5 hover:text-white'}`}>
                  {s === 'recent' ? '🕐 Récent' : s === 'popular' ? '🔥 Populaire' : '💬 Sans réponse'}
                </button>
              ))}
            </div>

            {/* Posts list */}
            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-2xl animate-pulse" />)}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 text-[#a0a0ab]">
                <MessageSquare size={36} className="mx-auto mb-3 opacity-40" />
                <p>Aucune discussion trouvée</p>
                {user && <button onClick={() => navigate('/communaute/nouveau')} className="text-[#ff6b6b] text-sm mt-2 hover:underline">Créer la première discussion</button>}
              </div>
            ) : (
              <div className="space-y-2">
                {posts.map(p => <PostCard key={p.post_id} post={p} onClick={() => navigate(`/communaute/${p.post_id}`)} />)}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex justify-center gap-2 pt-2">
                <button disabled={page === 1} onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('page', String(page - 1)); return n; })}
                  className="px-3 py-1.5 bg-white/5 rounded-lg text-xs text-[#e8e8e8] disabled:opacity-40">◀</button>
                <span className="px-3 py-1.5 text-xs text-[#a0a0ab]">{page}/{pages}</span>
                <button disabled={page === pages} onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('page', String(page + 1)); return n; })}
                  className="px-3 py-1.5 bg-white/5 rounded-lg text-xs text-[#e8e8e8] disabled:opacity-40">▶</button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Leaderboard */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2"><Trophy size={14} className="text-[#ffd93d]" /> Top contributeurs</h3>
              <div className="space-y-2">
                {leaders.slice(0, 5).map((l, i) => (
                  <div key={l.user_id} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#a0a0ab] w-4">{i + 1}</span>
                    <div className="w-6 h-6 rounded-full bg-[#ff6b6b]/20 flex items-center justify-center text-[#ff6b6b] text-xs font-bold">
                      {l.name?.[0]}
                    </div>
                    <span className="text-xs text-[#e8e8e8] flex-1 truncate">{l.name}</span>
                    <span className="text-xs text-[#a0a0ab]">{l.xp} XP</span>
                  </div>
                ))}
                {leaders.length === 0 && <p className="text-xs text-[#a0a0ab]">Pas encore de contributeurs</p>}
              </div>
            </div>

            {/* Categories list */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <h3 className="text-white text-sm font-semibold mb-3">Catégories</h3>
              <div className="space-y-1">
                {CATEGORIES.map(cat => (
                  <button key={cat.name}
                    onClick={() => setSearchParams(p => { const n = new URLSearchParams(p); n.set('category', cat.name); return n; })}
                    className="w-full text-left text-xs text-[#e8e8e8] hover:text-white flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5 transition-colors">
                    <span>{cat.icon}</span><span>{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
